import React, { useState, useMemo } from 'react';
import { Container, Typography, Grid, Button, Box, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import GitHubIcon from '@mui/icons-material/GitHub';
import LaunchIcon from '@mui/icons-material/Launch';

const FILTERS = [
  { key: 'all',         label: 'All',         count: 18 },
  { key: 'work',        label: 'Work',        count: 3  },
  { key: 'personal',    label: 'Personal',    count: 3  },
  { key: 'client',      label: 'Client',      count: 3  },
  { key: 'school',      label: 'School',      count: 6  },
  { key: 'volunteering',label: 'Volunteering',count: 3  },
];

const projects = [
  // ── Work ──────────────────────────────────────────────────────────────────
  {
    title: 'OutQuotes',
    description: 'A modern web application platform for managing quotes, orders, and customer relationships.',
    tags: ['Flask', 'Python', 'PostgreSQL', 'Docker', 'CI/CD', 'Azure', 'React', 'TailwindCSS'],
    demo: 'https://outquotes.com',
    image: '/images/outquotes.jpg',
    color: '#4a9eff',
    category: 'work',
    group: 'Templar Screens',
  },
  {
    title: 'Production Panel',
    description: 'Used at Templar Screens to manage each step in the screen building process.',
    tags: ['React', 'NodeJS', 'TypeScript', 'MongoDB', 'CI/CD', 'Azure'],
    image: '/images/production_panel.png',
    color: '#00c9ff',
    category: 'work',
    group: 'Templar Screens',
  },
  {
    title: 'Grid Master',
    description: 'A web application for managing daily tasks for the OutQuotes team.',
    tags: ['React', 'NodeJS', 'TypeScript', 'MongoDB', 'CI/CD', 'Azure'],
    image: '/images/grid_master.png',
    color: '#00ffa3',
    category: 'work',
    group: 'Templar Screens',
  },
  // ── Personal ──────────────────────────────────────────────────────────────
  {
    title: 'ZeroToil',
    description: 'A software studio that designs and ships real products: native apps, media tools, automation backends, and developer utilities. AI is one of the tools, not the whole toolbox. Also sells premium domains.',
    tags: ['Studio', 'Full-Stack', 'Automation', 'SaaS', 'Domains'],
    demo: 'https://zerotoil.com',
    image: '/images/zerotoil.png',
    color: '#bf5af2',
    category: 'personal',
    group: 'ZeroToil',
  },
  {
    title: 'BitAspire',
    description: 'A premium Minecraft plugin by ZeroToil, feature-rich game utilities built on SpigotAPI with 100,000+ downloads across major marketplaces.',
    tags: ['Java', 'Minecraft', 'SpigotAPI', 'SQL', 'YAML'],
    github: 'https://github.com/zerotoil',
    demo: 'https://wiki.bitaspire.com',
    image: '/images/bitaspire.png',
    color: '#00ffa3',
    category: 'personal',
    group: 'ZeroToil',
  },
  {
    title: 'My Wedding Website',
    description: 'A website for RSVP and registry information for my wedding built with React and NodeJS.',
    tags: ['React', 'NodeJS', 'Express', 'CI/CD', 'Pipeline'],
    demo: 'https://rsvp.yarmoshik.com',
    image: '/images/wedding_website.png',
    color: '#ffb347',
    category: 'personal',
    group: 'Personal',
  },
  // ── Client ────────────────────────────────────────────────────────────────
  {
    title: 'Spacer Maker',
    description: 'A full-stack spacer configurator that lets customers select templates, customize dimensions, bolt patterns, and materials, then submit orders for precision machined spacers.',
    tags: ['React', 'Full-Stack', 'E-Commerce', 'CAD', '3D Rendering'],
    demo: 'https://spacermaker.com',
    image: '/images/spacer_maker.png',
    color: '#ff9f0a',
    category: 'client',
    group: 'Standalone',
  },
  {
    title: 'Evergreen Christian Private School',
    description: 'Christ-centered K-8 school website with family portal integration, admissions flow, academic program pages, and bilingual community resources.',
    tags: ['HTML', 'CSS', 'JavaScript', 'Education'],
    demo: 'https://evergreenchristianps.org',
    image: '/images/evergreen_christian_private_school.png',
    color: '#30d158',
    category: 'client',
    group: 'Standalone',
  },
  {
    title: 'AutoPol',
    description: 'Bilingual (English/Ukrainian) website for a premium automotive body shop specializing in welding, painting, and expert vehicle restoration for any make or year.',
    tags: ['HTML', 'CSS', 'JavaScript', 'Bilingual'],
    demo: 'https://autopol.org',
    image: '/images/custom_websites/autopol-org.png',
    color: '#4dff88',
    category: 'client',
    group: 'Standalone',
  },
  // ── School ────────────────────────────────────────────────────────────────
  {
    title: 'Husky Huddle',
    description: 'A social platform for UW students to connect, collaborate, and share resources.',
    tags: ['JavaScript', 'React', 'NodeJS', 'SQL', 'API'],
    github: 'https://github.com/Kihsomray/huskyhuddle.com',
    demo: 'http://huskyhuddle.com',
    image: '/images/husky_huddle.png',
    color: '#ff3366',
    category: 'school',
    group: 'University of Washington',
  },
  {
    title: 'Dissonant World',
    description: 'A 2D RPG with free-to-play exploration, monster battles, and strategic resource use.',
    tags: ['JavaScript', 'HTML', 'CSS'],
    github: 'https://github.com/Kihsomray/dissonant.world',
    demo: 'https://dissonant.world',
    image: '/images/dissonant_world.png',
    color: '#9b4dff',
    category: 'school',
    group: 'University of Washington',
  },
  {
    title: 'SpreadSheet ADT',
    description: 'Dynamic spreadsheet engine with formula assignment and computation written in Java.',
    tags: ['Java', 'Swing'],
    github: 'https://github.com/Kihsomray/Spreadsheet-ADT',
    image: '/images/spreadsheet_adt.png',
    color: '#ff9d00',
    category: 'school',
    group: 'University of Washington',
  },
  {
    title: 'Inventory Management',
    description: 'An Inventory Management System (IMS) for a business that sells an assortment of goods.',
    tags: ['C#', 'SQL', 'MVC', '.NET', 'ASP'],
    github: 'https://github.com/Kihsomray/InventoryManagement',
    image: '/images/inventory_management.png',
    color: '#4a9eff',
    category: 'school',
    group: 'University of Washington',
  },
  {
    title: 'Dungeon Dash',
    description: 'A console turn-based dungeon exploration game utilizing best practices written in Java.',
    tags: ['Java', 'JavaFX'],
    github: 'https://github.com/Kihsomray/DungeonDash',
    image: '/images/dungeon_dash.png',
    color: '#ff8833',
    category: 'school',
    group: 'University of Washington',
  },
  {
    title: 'Dungeon Dash Saga',
    description: 'A maze game with varying difficulties and timed runs.',
    tags: ['JavaScript', 'GitHub Pages'],
    github: 'https://github.com/Kihsomray/DungeonDash-Saga',
    demo: 'https://dungeondash.net',
    image: '/images/dungeon_dash_saga.png',
    color: '#ff4d4d',
    category: 'school',
    group: 'University of Washington',
  },
  // ── Volunteering ──────────────────────────────────────────────────────────
  {
    title: 'Youth PNW',
    description: 'Cultivating Spiritual Growth, Community, and Unity within the Body of Christ.',
    tags: ['Squarespace', 'Google Maps', 'Website'],
    demo: 'https://www.youthpnw.com',
    image: '/images/youth_pnw.png',
    color: '#ff8833',
    category: 'volunteering',
    group: 'Volunteering',
  },
  {
    title: 'Simply Bible',
    description: 'A Discord bot simplifying scripture searches and verse retrieval.',
    tags: ['Python', 'Discord', 'JSON'],
    github: 'https://github.com/Kihsomray/SimplyBible',
    demo: 'http://simply-bible.com',
    image: '/images/simply_bible.png',
    color: '#4dff88',
    category: 'volunteering',
    group: 'Volunteering',
  },
  {
    title: 'Bible Psalm Offsets',
    description: 'ProPresenter Library for the book of Psalms that has fixed offsets, with a helper coded in Python.',
    tags: ['Python', 'ProPresenter'],
    github: 'https://github.com/Kihsomray/Psalm-Offsets-EN-RU',
    image: '/images/propresenter_offsets.png',
    color: '#00a5ff',
    category: 'volunteering',
    group: 'Volunteering',
  },
];

// ── Browser-window card ─────────────────────────────────────────────────────
function ProjectCard({ project, index }) {
  const [hovered, setHovered] = useState(false);

  const rawUrl = project.demo || project.github || '';
  const displayUrl = rawUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');

  return (
    <motion.div
      layout
      variants={{
        hidden:  { opacity: 0, y: 24, scale: 0.97 },
        visible: { opacity: 1, y: 0,  scale: 1, transition: { duration: 0.48, ease: [0.22, 1, 0.36, 1] } },
        exit:    { opacity: 0, scale: 0.95, transition: { duration: 0.2 } },
      }}
      initial="hidden"
      animate="visible"
      exit="exit"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Box sx={{
        borderRadius: '10px',
        overflow: 'hidden',
        background: 'rgba(28,28,30,0.85)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: hovered ? '1px solid rgba(255,255,255,0.18)' : '1px solid rgba(255,255,255,0.08)',
        boxShadow: hovered
          ? '0 24px 60px rgba(0,0,0,0.7), 0 2px 0 rgba(255,255,255,0.05) inset'
          : '0 4px 20px rgba(0,0,0,0.5)',
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        transition: 'transform 0.34s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.34s ease, box-shadow 0.34s ease',
      }}>

        {/* ── Browser chrome ── */}
        <Box sx={{
          height: 34,
          background: 'rgba(18,18,20,0.98)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          px: 1.4,
          gap: 1,
          flexShrink: 0,
        }}>
          {/* Traffic lights */}
          <Box sx={{ display: 'flex', gap: 0.55, flexShrink: 0 }}>
            {['#ff5f57', '#febc2e', '#28c840'].map((c) => (
              <Box key={c} sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: c,
                opacity: hovered ? 1 : 0.45,
                transition: 'opacity 0.3s ease',
              }} />
            ))}
          </Box>

          {/* URL bar */}
          <Box sx={{
            flex: 1,
            height: 20,
            borderRadius: '6px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.07)',
            display: 'flex',
            alignItems: 'center',
            px: 1,
            gap: 0.6,
            overflow: 'hidden',
          }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(255,255,255,0.3)', flexShrink: 0 }} />
            <Typography sx={{
              fontSize: '0.62rem',
              color: 'rgba(255,255,255,0.3)',
              fontFamily: '"SF Mono", ui-monospace, Menlo, Consolas, monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {displayUrl || 'private / internal'}
            </Typography>
          </Box>

          {/* Index */}
          <Typography sx={{
            fontSize: '0.6rem',
            color: 'rgba(255,255,255,0.22)',
            flexShrink: 0,
            letterSpacing: '0.05em',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {String(index + 1).padStart(2, '0')}
          </Typography>
        </Box>

        {/* ── Screenshot area ── */}
        <Box sx={{ position: 'relative', overflow: 'hidden' }}>
          <Box sx={{
            height: 190,
            backgroundImage: `url(${project.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            transform: hovered ? 'scale(1.05)' : 'scale(1)',
            transition: 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
          }} />

          {/* Hover description overlay - clean Apple dark */}
          <Box sx={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.88)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            opacity: hovered ? 1 : 0,
            transition: 'opacity 0.28s ease',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            p: 2.5,
          }}>
            <Typography sx={{
              color: 'rgba(245,245,247,0.82)',
              fontSize: '0.88rem',
              lineHeight: 1.72,
              mb: 2,
              letterSpacing: '-0.005em',
            }}>
              {project.description}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {project.github && (
                <Button
                  size="small"
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<GitHubIcon sx={{ fontSize: '0.9rem !important' }} />}
                  sx={{
                    color: 'rgba(245,245,247,0.82)',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.14)',
                    borderRadius: '8px',
                    fontSize: '0.78rem',
                    textTransform: 'none',
                    fontWeight: 400,
                    px: 1.5,
                    py: 0.5,
                    boxShadow: 'none',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.14)',
                      border: '1px solid rgba(255,255,255,0.24)',
                      color: '#fff',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Code
                </Button>
              )}
              {project.demo && (
                <Button
                  size="small"
                  href={project.demo}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<LaunchIcon sx={{ fontSize: '0.9rem !important' }} />}
                  sx={{
                    color: '#fff',
                    background: '#2997FF',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.78rem',
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 1.8,
                    py: 0.5,
                    boxShadow: 'none',
                    '&:hover': {
                      background: '#1a84e8',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Live Demo
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* ── Footer ── */}
        <Box sx={{
          px: 2,
          pt: 1.6,
          pb: 1.8,
          borderTop: '1px solid rgba(255,255,255,0.06)',
          background: 'transparent',
        }}>
          <Typography sx={{
            color: hovered ? '#F5F5F7' : 'rgba(245,245,247,0.88)',
            fontWeight: 600,
            fontSize: '0.97rem',
            letterSpacing: '-0.01em',
            mb: 1,
            transition: 'color 0.2s ease',
          }}>
            {project.title}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {project.tags.slice(0, 4).map((tag) => (
              <Typography key={tag} sx={{
                fontSize: '0.67rem',
                color: 'rgba(245,245,247,0.45)',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.08)',
                px: 0.9,
                py: 0.22,
                borderRadius: '5px',
                fontWeight: 400,
                letterSpacing: '-0.005em',
              }}>
                {tag}
              </Typography>
            ))}
            {project.tags.length > 4 && (
              <Typography sx={{ fontSize: '0.67rem', color: 'rgba(245,245,247,0.25)', alignSelf: 'center', pl: 0.2 }}>
                +{project.tags.length - 4}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export default function Projects() {
  const theme = useTheme();
  const [active, setActive] = useState('all');

  const visible = useMemo(
    () => active === 'all' ? projects : projects.filter((p) => p.category === active),
    [active],
  );

  const groups = useMemo(() => {
    if (active === 'all') return null;
    return visible.reduce((acc, p) => {
      acc[p.group] = acc[p.group] || [];
      acc[p.group].push(p);
      return acc;
    }, {});
  }, [active, visible]);

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 8, md: 12 }, mb: 10 }}>
      {/* ── Header ── */}
      <motion.div
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography sx={{
            fontSize: '0.72rem',
            fontWeight: 500,
            color: '#2997FF',
            textTransform: 'uppercase',
            letterSpacing: '0.16em',
            mb: 1.5,
          }}>
            Portfolio
          </Typography>
          <Typography variant="h2" sx={{ mb: 1.5, color: '#F5F5F7' }}>
            Featured Projects
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, maxWidth: 500, mx: 'auto' }}>
            Selected work spanning full‑stack apps, developer tooling, and web platforms
          </Typography>
        </Box>
      </motion.div>

      {/* ── Filter bar ── */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 0.8,
          mb: 6,
          p: 0.65,
          borderRadius: '10px',
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.08)',
          width: 'fit-content',
          mx: 'auto',
        }}>
          {FILTERS.map(({ key, label, count }) => {
            const isActive = active === key;
            return (
              <Box
                key={key}
                component="button"
                onClick={() => setActive(key)}
                sx={{
                  px: 2,
                  py: 0.75,
                  borderRadius: '10px',
                  border: 'none',
                  background: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                  color: isActive ? '#F5F5F7' : 'rgba(245,245,247,0.45)',
                  fontSize: '0.84rem',
                  fontFamily: 'inherit',
                  fontWeight: isActive ? 500 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.18s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.7,
                  boxShadow: 'none',
                  outline: 'none',
                  letterSpacing: '-0.01em',
                  '&:hover': { color: '#F5F5F7', background: 'rgba(255,255,255,0.07)' },
                }}
              >
                {label}
                <Box component="span" sx={{
                  fontSize: '0.66rem',
                  px: 0.65,
                  py: 0.05,
                  borderRadius: '4px',
                  background: isActive ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.05)',
                  color: isActive ? 'rgba(245,245,247,0.8)' : 'rgba(245,245,247,0.3)',
                  fontFamily: 'inherit',
                  lineHeight: 1.7,
                }}>
                  {count}
                </Box>
              </Box>
            );
          })}
        </Box>
      </motion.div>

      {/* ── Cards ── */}
      {groups ? (
        Object.entries(groups).map(([groupName, groupProjects]) => (
          <Box key={groupName} sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Typography sx={{
                fontSize: '0.7rem',
                fontWeight: 500,
                color: 'rgba(245,245,247,0.3)',
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                whiteSpace: 'nowrap',
              }}>
                {groupName}
              </Typography>
              <Box sx={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
            </Box>
            <Grid container spacing={3}>
              <AnimatePresence mode="popLayout">
                {groupProjects.map((project, i) => (
                  <Grid item xs={12} sm={6} md={4} key={project.title}>
                    <ProjectCard project={project} index={i} />
                  </Grid>
                ))}
              </AnimatePresence>
            </Grid>
          </Box>
        ))
      ) : (
        <Grid container spacing={3}>
          <AnimatePresence mode="popLayout">
            {visible.map((project, i) => (
              <Grid item xs={12} sm={6} md={4} key={project.title}>
                <ProjectCard project={project} index={i} />
              </Grid>
            ))}
          </AnimatePresence>
        </Grid>
      )}
    </Container>
  );
}
